import { useState } from 'react'

import { useRequestTrack, useToggleFavorite } from '@/api/hooks'
import type { LibraryItem, PlaySource, SearchResult } from '@/api/types'
import { usePendingPlayback } from '@/audio/pendingPlayback'
import { toQueueItem, usePlayer } from '@/audio/store'
import { toast } from '@/components/ui'
import { AddToPlaylistDialog, type AddToPlaylistTarget } from '@/features/search/AddToPlaylistDialog'
import { describeError } from '@/features/search/describeError'
import { SearchResultRow, type ResultAction } from '@/features/search/SearchResultRow'

export function ArtistSongs({ tracks, label = 'Artist songs', source = 'search', libraryItems = [], onPlayTrack, selected, onToggleSelection }: { tracks: SearchResult[]; label?: string; source?: PlaySource; libraryItems?: LibraryItem[]; onPlayTrack?: (track: SearchResult) => void; selected?: Set<string>; onToggleSelection?: (key: string) => void }) {
  const addTrack = useRequestTrack()
  const favorite = useToggleFavorite()
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  const [errors, setErrors] = useState<Record<string, { message: string; action: ResultAction }>>({})
  const [target, setTarget] = useState<AddToPlaylistTarget | null>(null)

  async function act(track: SearchResult, action: ResultAction) {
    const key = `${track.provider}:${track.external_id}`
    if (busy[key]) return
    setBusy((state) => ({ ...state, [key]: true }))
    dismiss(key)
    try {
      const { library_item } = await addTrack.mutateAsync({
        intent: action === 'download' ? 'save' : 'play',
        provider: track.provider, external_id: track.external_id,
      })
      const item = toQueueItem(library_item)
      if (!item) {
        if (action !== 'download') {
          usePendingPlayback.getState().requestWhenReady(library_item.id, action, source)
        }
        toast.info(`Downloading ${track.title}…`, 'Follow its progress in Downloads.')
        return
      }
      const player = usePlayer.getState()
      if (action === 'play') player.playQueue([item], 0, { source })
      if (action === 'next') {
        player.playNextUp([item])
        toast.success('Playing next', track.title)
      }
      if (action === 'queue') {
        player.enqueue([item])
        toast.success('Added to queue', track.title)
      }
      if (action === 'download') toast.success('Added to library', track.title)
    } catch (error) {
      setErrors((state) => ({ ...state, [key]: {
        message: describeError(error, 'Could not add this song.'), action,
      } }))
    } finally {
      setBusy((state) => ({ ...state, [key]: false }))
    }
  }

  function dismiss(key: string) {
    setErrors(({ [key]: _removed, ...rest }) => rest)
  }

  return (
    <>
      <ul aria-label={label} className="flex flex-col">
        {tracks.map((track) => {
          const key = `${track.provider}:${track.external_id}`
          const item = libraryItems.find((item) => item.track.id === track.track_id)
          return <SearchResultRow
            key={key}
            result={track}
            isFavorite={item?.is_favorite}
            onToggleFavorite={item ? () => favorite.mutate({ id: item.id, favorite: !item.is_favorite }, { onError: (error) => toast.error('Could not update favourite', describeError(error, 'Try again.')) }) : undefined}
            selectable={!!selected}
            selected={selected?.has(key)}
            onSelectChange={onToggleSelection ? () => onToggleSelection(key) : undefined}
            status={track.library_status ?? undefined}
            providerName={track.provider}
            busy={busy[key]}
            error={errors[key] ? {
              message: errors[key].message,
              retry: () => void act(track, errors[key].action),
            } : undefined}
            onAction={(action) => { if (action === 'play' && onPlayTrack) onPlayTrack(track); else void act(track, action) }}
            onDismissError={() => dismiss(key)}
            onAddToPlaylist={() => setTarget({
              title: track.title, provider: track.provider,
              externalId: track.external_id, trackId: track.track_id,
            })}
          />
        })}
      </ul>
      {target ? <AddToPlaylistDialog target={target} onOpenChange={(open) => {
        if (!open) setTarget(null)
      }} /> : null}
    </>
  )
}
